import { isMobile } from '@/biz/tool';
import {
  AiReviewNoDataIcon,
  BrandVerifiedIcon,
  CloseIcon
} from '@/components/common/icon';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebModal from '../Modal';
import { AudienceViewSource } from './config';

const countryCodeToFlag = (code: string) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const DONUT_COLORS = ['#7570B3', '#A6CEE3', '#E6AB02', '#1B9E77'];

const GENDERS_COLORS_ = ['#60A5FA', '#C084FC'];

const DonutChart = ({
  data,
  size = 80,
  boxSize = 100
}: {
  data: { name: string; value: number; color: string }[];
  size?: number;
  boxSize?: number;
}) => {
  const r = (size - 8) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const segmentGap = data.length > 1 ? 2 : 0;
  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`-10 -10 ${boxSize} ${boxSize}`}>
      {data.map((item, i) => {
        const pct = item.value / 100;
        const dashLen = Math.max(0, circumference * pct - segmentGap);
        const dashOffset =
          circumference * (1 - accumulated) + circumference * 0.25;
        accumulated += pct;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={item.color}
            strokeWidth={size * 0.18}
            strokeDasharray={`${dashLen} ${circumference - dashLen}`}
            strokeDashoffset={dashOffset}
            style={{
              transition: 'stroke-dasharray 0.4s, stroke-dashoffset 0.4s'
            }}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={r - size * 0.13} fill="white" />
    </svg>
  );
};

export const AudienceCharts = ({
  audienceData,
  variant = 'default'
}: {
  audienceData: any;
  variant?: 'default' | 'channel';
}) => {
  const { t } = useTranslation();
  const [isFirstParty, setIsFirstParty] = useState(false);
  const countries: any[] = audienceData?.countries || [];
  const genders: any[] = audienceData?.genders || [];
  const ages: any[] = audienceData?.ages || [];

  const genderData = useMemo(
    () =>
      genders.map((g, i) => ({
        name: g.name === 'female' ? 'F' : g.name === 'male' ? 'M' : g.name,
        value: parseFloat(g.value) || 0,
        color: GENDERS_COLORS_[i % GENDERS_COLORS_.length]
      })),
    [genders]
  );

  const ageData = useMemo(
    () =>
      ages.map((a, i) => ({
        name: a.name,
        value: parseFloat(a.value) || 0,
        color: DONUT_COLORS[i % DONUT_COLORS.length]
      })),
    [ages]
  );

  const maxCountryValue = useMemo(
    () => Math.max(...countries.map((c) => parseFloat(c.value) || 0), 1),
    [countries]
  );

  useEffect(() => {
    setIsFirstParty(audienceData?.source === AudienceViewSource.FIRST_PARTY);
  }, [audienceData]);

  if (!countries.length && !genders.length && !ages.length) return null;

  return (
    <div className="w-full px-4 py-3">
      {variant === 'default' ? (
        <div className="w-full flex items-center mb-3">
          <div className="text-[#0D0D0D] text-sm font-semibold border-r border-[#E5E5E5] pr-2 mr-2">
            {t('modal.modal_take_order.audience_demographics.details', {
              defaultValue: 'Details'
            })}
          </div>
          {!isFirstParty ? (
            <div className="flex items-center border border-[#E9D5FF] bg-[#FAF5FF] h-[22px] rounded-full px-2 w-fit">
              <AiReviewNoDataIcon className="w-3 h-3 text-[#7E22CE]" />
              <div className="ml-1 text-center text-[#7E22CE] text-xs font-medium font-['DM_Sans'] leading-4 gap-1 flex items-center">
                {t('modal.modal_take_order.audience_demographics.ai_estimate', {
                  defaultValue: 'AI estimate'
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center border border-[#ABEFC6] bg-[#ECFDF3] h-[22px] rounded-full px-2 w-fit">
              <div className="text-center text-[#067647] text-xs font-medium font-['DM_Sans'] leading-4 gap-1 flex items-center">
                <BrandVerifiedIcon className="w-[14px] h-[14px] text-[#22C55E]" />
                {t(
                  'modal.modal_take_order.audience_demographics.verified_data',
                  {
                    defaultValue: 'Verified data'
                  }
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}
      <div
        className={`w-full bg-white rounded-xl flex gap-2 ${variant === 'channel' ? 'flex-col md:flex-row' : isMobile() ? 'flex-col' : 'flex-row'}`}
      >
        {/* Top locations */}
        {countries.length > 0 && (
          <div
            className={`flex-1 ${variant === 'channel' || isMobile() ? 'max-w-full' : 'max-w-[280px]'} min-w-0 bg-[#FAFAFA] px-4 py-3 rounded-lg`}
          >
            <div className="text-[#0D0D0D] text-sm font-medium mb-3">
              {t('modal.modal_take_order.audience_demographics.top_locations', {
                defaultValue: 'Top 10 locations'
              })}
            </div>
            <div className="flex flex-col gap-[6px]">
              {countries.slice(0, 10).map((c: any) => {
                const pct = parseFloat(c.value) || 0;
                const barWidth = (pct / maxCountryValue) * 100;
                return (
                  <div key={c.code} className="flex items-center gap-2">
                    <span className="text-base leading-none w-6 text-center shrink-0">
                      {countryCodeToFlag(c.code)}
                    </span>
                    <span className="text-xs text-[#0D0D0D] font-medium w-8 shrink-0">
                      {c.code}
                    </span>
                    <div className="flex-1 h-3 bg-[#F5F5F5] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#FB6011]"
                        style={{
                          width: `${barWidth}%`,
                          transition: 'width 0.4s'
                        }}
                      />
                    </div>
                    <span className="text-xs text-[#0D0D0D] font-medium w-12 text-right shrink-0">
                      {variant === 'channel'
                        ? new Intl.NumberFormat('en-US', {
                            maximumFractionDigits: 2
                          }).format(pct)
                        : pct.toFixed(pct >= 10 ? 0 : 2)}
                      %
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Gender + Age */}
        {(genders.length > 0 || ages.length > 0) && (
          <div className="min-w-[220px] flex flex-col gap-2 shrink-0">
            {genders.length > 0 && (
              <div className="bg-[#FAFAFA] px-4 py-3 rounded-lg">
                <div className="text-[#0D0D0D] text-sm font-medium mb-3">
                  {t('modal.modal_take_order.audience_demographics.gender', {
                    defaultValue: 'Gender'
                  })}
                </div>
                <div className="flex items-center gap-4">
                  <DonutChart data={genderData} size={80} boxSize={100} />
                  <div className="flex flex-col gap-1">
                    {genderData.map((g) => (
                      <div
                        key={g.name}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: g.color }}
                        />
                        <span className="text-[#0D0D0D] font-normal">
                          {g.value.toFixed(1)}% {g.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {ages.length > 0 && (
              <div className="min-w-[220px] bg-[#FAFAFA] px-4 py-3 rounded-lg">
                <div className="text-[#0D0D0D] text-sm font-medium mb-3">
                  {t('modal.modal_take_order.audience_demographics.age', {
                    defaultValue: 'Age'
                  })}
                </div>
                <div className="flex items-center gap-4">
                  <DonutChart data={ageData} size={80} boxSize={100} />
                  <div className="flex flex-col gap-1">
                    {ageData.map((a) => (
                      <div
                        key={a.name}
                        className="flex items-center gap-2 text-xs font-normal"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: a.color }}
                        />
                        <span className="text-[#0D0D0D] font-normal">
                          {a.name}{' '}
                          {t(
                            'modal.modal_take_order.audience_demographics.yrs',
                            {
                              defaultValue: 'yrs'
                            }
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ModalShowImg = ({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) => {
  const AudienceInsightsImage = () => {
    return (
      <div className="max-w-full">
        <img
          alt="audience-insights"
          src={'/audience-insights.png'}
          className="w-full h-auto object-contain"
        />
      </div>
    );
  };

  return (
    <WebModal
      visible={isOpen}
      canClose={true}
      handleClose={() => setIsOpen(false)}
      isShowFooter={false}
      isShowHeader={false}
      width={isMobile() ? 375 : undefined}
      className={`!z-[9999]
        ${isMobile() ? 'p-0' : 'p-0'}
        `}
    >
      <div className="absolute -top-12 right-2 rounded-full p-2 cursor-pointer hover:bg-gray-200">
        <CloseIcon className="w-6 h-6" onClick={() => setIsOpen(false)} />
      </div>
      <AudienceInsightsImage />
    </WebModal>
  );
};
